
import throttle from 'lodash/throttle'

export default {
  name: 'EoSlider',
  inheritAttrs: false, // place additional attrs not on container element, but on <input>
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    /**
     * Current value of the slider
     */
    value: {
      type: [String, Number],
      default: 0,
    },
    /**
     * Visible format of the value
     */
    outputFormat: {
      type: Function,
      default: (v) => { if (v === null || v === undefined) return '-'; return v },
    },
    /**
     * Minimal value of the slider
     */
    min: {
      type: [String, Number],
      default: 0,
    },
    /**
     * Maximum value of the slider
     */
    max: {
      type: [String, Number],
      default: 10,
    },
    /**
     * Size of each step on the slider
     */
    step: {
      type: [String, Number],
      default: null,
    },
    
    /**
     * Guiding label next to the slider
     */
    label: {
      type: String,
      default: null,
      required: true,
    },
    /**
     * Visually hide the label, but make it visible for screen readers
     */
    labelHidden: {
      type: Boolean,
      default: false,
    },
    /**
     * Visually hide the output, but make it visible for screen readers
     */
    outputHidden: {
      type: Boolean,
      default: false,
    },
    /**
     * Description of the slider (a visual hint)
     */
    description: {
      type: String,
      default: null,
    },
    /**
     * The name attribute
     */
    name: {
      type: String,
      default: null,
    },
    /**
     * Autofocus this slider on page load
     */
    autofocus: {
      type: Boolean,
      default: false,
    },
    /**
     * Show the slider in error state
     */
    error: {
      type: Boolean,
      default: false,
    },
    /**
     * Array of messages to display beneath the slider
     */
    errorMessages: {
      type: Array,
      default: () => [],
    },
    /**
     * Prevent user interaction with the slider
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Minimum width (1-12 OR pixels)
     */
    minWidth: {
      type: [String, Number],
      default: null,
      validator: (value) => {
        if (Number(value)) {
          return 0 <= Number(value) && Number(value) <= 12
        }
        return true
      },
    },
    /**
     * Maximum width (1-12 OR pixels)
     */
    maxWidth: {
      type: [String, Number],
      default: null,
      validator: (value) => {
        if (Number(value)) {
          return 0 <= Number(value) && Number(value) <= 12
        }
        return true
      },
    },
    /**
     * Color of the track
     */
    color: {
      type: String,
      default: 'grey-800',
    },
    /**
     * Color of the interactive elements
     */
    accentColor: {
      type: String,
      default: 'purple-500',
    },
    /**
     * Color of the texts
     */
    textColor: {
      type: String,
      default: null,
    },
  },
  data () {
    return {
      id: null,
      mValue: null,
      isUserSliding: false,
    }
  },
  computed: {
    cClasses () {
      return {
        'eo-slider': true,
        'is-disabled': this.disabled,
        ['text--' + this.textColor]: !!this.textColor,
      }
    },
    cStyles () {
      return {
        '--position': this.cPosition,
        '--color': this.$color[this.color],
        '--color-accent': this.$color[this.accentColor],
      }
    },
    cStylesInput () {
      return {
        minWidth: this.convertWidthToCss(this.minWidth),
        maxWidth: this.convertWidthToCss(this.maxWidth),
        width: this.maxWidth ? '100%' : 'auto',
        flexGrow: this.maxWidth ? '0' : '1',
      }
    },
    cClassesLabel () {
      return {
        'eo-slider__label': true,
        'display--sr': !!this.labelHidden,
      }
    },
    cClassesOutput () {
      return {
        'eo-slider__output': true,
        'display--sr': !!this.outputHidden,
      }
    },
    cPosition () {
      if (this.mValue === null || this.mValue === undefined) return '50%'
      return `${Math.abs(this.mValue - this.min) / Math.abs(this.max - this.min) * 100}%`
    },
    cName () {
      return this.name || this.id
    },
    cMessagesId () {
      return `messages-${this.id}`
    },
  },
  watch: {
    /**
     * Watch the prop and sync component state with it
     */
    value: {
      handler: function (value) {
        if (!this.isUserSliding) {
          this.mValue = value
        }
      },
      immediate: true,
    },
  },
  mounted () {
    this.id = this.$attrs.id || `slider-${(this.name ? this.name + '-' : '') + this._uid}`
  },
  methods: {
    onInput: throttle(function (e) {
      /**
       * Emitted while the value is being altered
       * @event input
       */
      this.mValue = e.target.value
      this.$emit('input', this.mValue)
    }, 10),
    onChange (e) {
      /**
       * Emitted when the value has been altered
       * @event change
       */
      this.isUserSliding = false
      this.mValue = e.target.value
      this.$emit('change', e.target.value)
    },
    onFocus (e) {
      /**
       * Emitted when the user focusses on the slider
       * @event focus
       */
      this.$emit('focus', e)
      this.trackFocus(true)
    },
    onBlur (e) {
      /**
       * Emitted when the user removes focus from the slider
       * @event blur
       */
      this.$emit('blur', e)
      this.trackFocus(false)
    },
    onTouchstart () {
      /**
       * Emitted when the user touches the slider
       * @event touchstart
       */
      this.isUserSliding = true
    },
    onTouchend () {
      /**
       * Emitted when the user releases the slider
       * @event touchend
       */
      this.isUserSliding = false
    },
    onKeydown (e) {
      /**
       * Emitted when the user types in the slider
       * @event keydown
       */
      this.$emit('keydown', e)
    },
    onKeyup (e) {
      this.$emit('keyup', e)
    },
    convertWidthToCss (size) {
      /**
       * Convert size to pixels when a integer has been given
       * @property {String} size - number from 1 to 12 or a fixed amount of 'px'
       */
      if (!size) return null
      if (Number(size) && 0 < size && size <= 12) {
        return (100 / 12 * size) + '%'
      }
      if (typeof size === 'string' && (size.includes('px') || size.includes('calc'))) {
        return size
      }
      return size + 'px'
    },
    trackFocus (value) {
      this.isFocus = value
    },
    focus () {
      this.$refs.input.focus()
    },
    blur () {
      this.$refs.input.blur()
    },
  },
}
